import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LabeledToggleSwitch } from '@/components/ui/LabeledToggleSwitch';
import { Modal } from '@/components/ui/Modal';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { inputClass, labelClass, listRowClass, inlineErrorClass } from '@/components/ui/inputStyles';
import { useSession } from '@/hooks/useSession';
import { PromotionAreaRobotsModal } from '@/components/PromotionAreaRobotsModal';

const PER_PAGE = 15;

type Row = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
  created_at?: string;
  owner_username?: string | null;
  robot_count?: number;
};

export function PromotionAreasPage() {
  const [searchParams] = useSearchParams();
  const { capabilities } = useSession();
  const isSysAdmin = capabilities.manage_users;
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [robotsModalArea, setRobotsModalArea] = useState<Row | null>(null);
  const activeId = Number(searchParams.get('active_id') || 0);
  const activeViewRaw = searchParams.get('active_view');
  const activeView = activeViewRaw === 'entries' ? 'entries' : activeViewRaw === 'logs' ? 'logs' : 'config';

  const load = useCallback(async () => {
    setErr('');
    const r = await apiFetch<{ ok: boolean; items: Row[]; total: number }>('list_promotion_areas', {
      method: 'GET',
      query: { page, per_page: PER_PAGE },
    });
    if (r.ok) {
      setItems(r.items);
      setTotal(r.total);
    } else {
      setErr('加载失败');
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function submitAdd() {
    const n = addName.trim();
    if (!n) return;
    setAddSaving(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; id?: number; err?: string }>('create_promotion_area', {
        method: 'POST',
        body: JSON.stringify({ name: n }),
      });
      if (!r.ok) {
        setErr('创建失败：' + (r.err || 'unknown'));
        return;
      }
      setAddOpen(false);
      setAddName('');
      setPage(1);
      await load();
    } finally {
      setAddSaving(false);
    }
  }

  async function toggleEnabled(row: Row) {
    const next = !row.enabled;
    if (!next && !window.confirm(`确定停用推广区域「${row.name}」？`)) {
      return;
    }
    setTogglingId(row.id);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_promotion_area', {
        method: 'POST',
        body: JSON.stringify({ id: row.id, enabled: next }),
      });
      if (!r.ok) {
        setErr('更新启用状态失败');
        return;
      }
      await load();
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="微推广管理"
        description="按推广区域管理微推广信息：每个区域可独立配置「图文信息」与「招聘信息」两套金数据表单、字段映射与推广条目。"
      />
      {err ? <p className={inlineErrorClass}>{err}</p> : null}
      <Card
        title="推广区域列表"
        titleExtra={
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/micro-promotion-forward-logs">
              <Button type="button" variant="secondary">
                转发日志
              </Button>
            </Link>
            <Link to="/micro-promotion-wework-push-logs">
              <Button type="button" variant="secondary">
                企微推送日志
              </Button>
            </Link>
            <Button type="button" variant="primary" onClick={() => setAddOpen(true)}>
              新建推广区域
            </Button>
          </div>
        }
      >
        {items.length === 0 ? (
          <p className="text-sm text-slate-600">暂无记录，请点击上方「新建推广区域」。</p>
        ) : (
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {items.map((it) => {
              const isActiveRow = activeId === it.id;
              const baseBtnCls =
                'inline-flex min-w-[104px] justify-center rounded-lg border px-3 py-2 text-sm font-medium shadow-sm transition-all duration-150 hover:-translate-y-px active:translate-y-0';
              const logsCls =
                isActiveRow && activeView === 'logs'
                  ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                  : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
              const configCls =
                isActiveRow && activeView === 'config'
                  ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                  : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
              const entriesCls =
                isActiveRow && activeView === 'entries'
                  ? `${baseBtnCls} border-slate-900 bg-slate-900 text-white shadow-md hover:bg-slate-800`
                  : `${baseBtnCls} border-slate-300 bg-white text-slate-700 hover:bg-slate-50`;
              return (
                <li key={it.id} className={listRowClass}>
                  <div className="flex w-full flex-wrap items-start gap-3 md:flex-nowrap md:items-center">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900">{it.name}</p>
                      <p className="mt-0.5 font-mono text-xs text-slate-500">
                        区域 ID：<span className="select-all">{it.public_id}</span>
                      </p>
                      {isSysAdmin && it.owner_username ? (
                        <p className="mt-0.5 text-xs text-slate-400">归属：{it.owner_username}</p>
                      ) : null}
                      {it.created_at ? <p className="mt-1 text-xs text-slate-500">{it.created_at}</p> : null}
                      {typeof it.robot_count === 'number' ? (
                        <p className="mt-1 text-xs text-slate-500">
                          已绑定机器人：<span className="font-medium text-slate-700">{it.robot_count}</span> 台
                        </p>
                      ) : null}
                    </div>
                    <div className="ml-auto flex w-full flex-wrap items-center justify-end gap-2 md:w-auto md:flex-nowrap">
                      <LabeledToggleSwitch
                        checked={it.enabled}
                        busy={togglingId === it.id}
                        aria-label={`${it.name}：${it.enabled ? '已开启' : '已关闭'}`}
                        onChange={() => void toggleEnabled(it)}
                      />
                      <Link
                        to={`/micro-promotion-forward-logs?area_id=${it.id}&active_id=${it.id}`}
                        className={logsCls}
                      >
                        转发日志
                      </Link>
                      <Link to={`/promotion-areas/${it.id}?view=config`} className={configCls}>
                        参数配置
                      </Link>
                      <Link to={`/promotion-areas/${it.id}?view=entries`} className={entriesCls}>
                        推广信息管理
                      </Link>
                      <Button type="button" variant="secondary" className={baseBtnCls} onClick={() => setRobotsModalArea(it)}>
                        配置机器人
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <PaginationBar
          className="mt-4"
          page={page}
          totalPages={Math.max(1, Math.ceil(total / PER_PAGE))}
          total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(Math.max(1, Math.ceil(total / PER_PAGE)), p + 1))}
        />
      </Card>

      {robotsModalArea ? (
        <PromotionAreaRobotsModal
          open
          areaId={robotsModalArea.id}
          areaName={robotsModalArea.name}
          onClose={() => setRobotsModalArea(null)}
          onSaved={() => void load()}
        />
      ) : null}

      <Modal open={addOpen} title="新建推广区域" onClose={() => !addSaving && setAddOpen(false)}>
        <div className="space-y-4">
          <div>
            <label className={labelClass} htmlFor="pa-name">
              名称
            </label>
            <input
              id="pa-name"
              className={inputClass}
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              placeholder="如：南京微推广"
            />
          </div>
          <p className="text-xs leading-relaxed text-slate-500">
            创建后将自动生成「图文信息」「招聘信息」两套独立参数与 Webhook 地址，可在参数配置中分别对接金数据表单。
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" disabled={addSaving} onClick={() => setAddOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={addSaving || !addName.trim()}
              onClick={() => void submitAdd()}
            >
              {addSaving ? '创建中…' : '创建'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
